import axios, { AxiosError, AxiosResponse } from 'axios'
import { useAuthStore } from '@stores/auth' // Used in response interceptor for logout

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

export const api = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests (needed for CSRF token)
})

// Helper function to get cookie value by name
function getCookie(name: string): string | undefined {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()?.split(';').shift()
  return undefined
}

// Request interceptor - add CSRF token (auth handled via httpOnly cookies)
api.interceptors.request.use(
  (config) => {
    // Add CSRF token for state-changing operations
    if (config.method && ['post', 'put', 'delete', 'patch'].includes(config.method.toLowerCase())) {
      const csrfToken = getCookie('csrfToken')
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor - handle errors globally
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      useAuthStore.getState().logout()
      window.location.href = '/login'
    } else if (error.response?.status === 403) {
      // Forbidden - log only status code, not sensitive data
      console.error('Access forbidden: HTTP 403')
    } else if (error.response?.status === 500) {
      // Server error - log only generic message, not sensitive data
      console.error('Server error: HTTP 500')
    }
    return Promise.reject(error)
  }
)

export interface ApiError {
  message: string
  status?: number
  errors?: Record<string, string[]>
}

export function handleApiError(error: unknown): ApiError {
  if (axios.isAxiosError(error)) {
    return {
      message: error.response?.data?.message || error.message || 'An error occurred',
      status: error.response?.status,
      errors: error.response?.data?.errors,
    }
  }
  return {
    message: 'An unexpected error occurred',
  }
}
